/**
 * Consent change bus. The cookie banner (`useCookieConsent`) is the only
 * writer of consent; consumers that live elsewhere in the tree (the analytics
 * runtime, the Portfolio tracker) read the stored decision once and then
 * react to this window event, so a grant or a withdrawal takes effect
 * immediately, without a reload.
 */
import { consentCookieName, parseStoredConsent } from './anonymous-state'
import type { ConsentState } from './anonymous-state'
import { getCookie } from './cookies'

export const CONSENT_CHANGE_EVENT = 'ae:consent-change'

/** The stored, current-version consent decision, or `null` (none yet). */
export function readStoredConsent(): ConsentState | null {
  return parseStoredConsent(getCookie(consentCookieName))
}

export function emitConsentChange(consent: ConsentState): void {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(
      new CustomEvent<ConsentState>(CONSENT_CHANGE_EVENT, { detail: consent }),
    )
  } catch {
    // ignore
  }
}

export function onConsentChange(listener: (consent: ConsentState) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<ConsentState | null>).detail
    if (detail) listener(detail)
  }
  window.addEventListener(CONSENT_CHANGE_EVENT, handler)
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler)
}
